import React, { useState, useEffect, useRef } from "react";
import { api, formatDKK } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import BookingDialog from "@/components/BookingDialog";
import { MapPin, Info, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

const REASON_KEY = {
  heavy_interior: "reasonHeavy", sliding_doors: "reasonSliding",
  lighting: "reasonLighting", unknown_components: "reasonUnknown", large_order: "reasonLarge",
  low_confidence: "reasonLowConfidence",
};

// Shared price panel. Calls the SINGLE backend pricing engine for both the
// manual calculator and the AI-upload flow. Shows a FIXED price for standard
// configs, and a PRELIMINARY price (never a guaranteed final price) when the
// backend flags manual_price_review.
export default function PaxPricePanel({ config, postcode, setPostcode, source = "manual", recognizedComponents = [], fileIds = [] }) {
  const { t } = useLang();
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    setCalculating(true);
    timer.current = setTimeout(async () => {
      try {
        const res = await api.post("/pricing/pax/calculate", { configuration: config, postcode });
        setResult(res.data);
      } catch (e) { /* ignore */ }
      finally { setCalculating(false); }
    }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [config, postcode]);

  const preliminary = result?.is_preliminary;

  return (
    <div className="lg:sticky lg:top-24 bg-[#1E3A2F] text-white rounded-2xl p-6 sm:p-7" data-testid="pax-price-panel">
      <div className="relative mb-4">
        <MapPin className="w-5 h-5 text-white/50 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={postcode}
          onChange={(e) => setPostcode(e.target.value)}
          inputMode="numeric"
          placeholder={t.common.postcode}
          className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-white/60 outline-none"
          data-testid="pax-postcode-input"
        />
      </div>

      <div className="text-xs uppercase tracking-widest text-white/50 font-semibold" data-testid="pax-price-label">
        {preliminary ? t.pax.preliminaryPrice : t.pax.fixedPrice}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="font-mono font-bold text-4xl sm:text-5xl" data-testid="pax-price-value">
          {result ? formatDKK(result.price_incl_vat) : "—"}
        </span>
        {calculating && <Loader2 className="w-4 h-4 animate-spin text-white/50" />}
      </div>
      <p className={`text-xs mt-1 ${preliminary ? "text-amber-300" : "text-white/60"}`} data-testid="pax-price-note">
        {preliminary ? t.pax.preliminaryNote : t.pax.fixedNote}
      </p>
      {preliminary && <p className="text-xs text-white/50 mt-0.5" data-testid="pax-incl-vat">{t.pax.inclVat}</p>}

      {result && (
        <div className="mt-4 space-y-1.5 text-sm border-t border-white/15 pt-4" data-testid="pax-summary">
          <div className="flex justify-between text-white/80">
            <span>{result.sections} × {t.pax.sections.toLowerCase()}</span>
            <span className="font-mono">{formatDKK(result.base_price_incl_vat)}</span>
          </div>
          {result.travel_surcharge_incl_vat > 0 && (
            <div className="flex justify-between text-white/80">
              <span>{t.pax.travel}{result.zone_name ? ` — ${result.zone_name}` : ""}</span>
              <span className="font-mono">{formatDKK(result.travel_surcharge_incl_vat)}</span>
            </div>
          )}
          {result.packaging_surcharge_incl_vat > 0 && (
            <div className="flex justify-between text-white/80">
              <span>{t.pax.packagingRemoval}</span>
              <span className="font-mono">{formatDKK(result.packaging_surcharge_incl_vat)}</span>
            </div>
          )}
        </div>
      )}

      {preliminary && (
        <div className="mt-4 bg-amber-400/15 border border-amber-300/30 rounded-xl p-3" data-testid="pax-review-box">
          <div className="flex items-center gap-1.5 text-amber-200 text-sm font-semibold"><AlertTriangle className="w-4 h-4" /> {t.pax.reviewIntro}</div>
          <ul className="mt-1.5 space-y-1 text-xs text-amber-100/90">
            {result.review_reasons.map((r) => <li key={r}>• {t.pax[REASON_KEY[r]] || r}</li>)}
          </ul>
        </div>
      )}

      {!postcode && <p className="mt-3 text-xs text-white/60 flex items-start gap-1.5"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {t.pax.enterPostcode}</p>}
      {postcode && result?.zone_supported === false && <p className="mt-3 text-xs text-amber-200">{t.pax.notSupported}</p>}

      <div className="mt-3 inline-flex items-center gap-1.5 text-xs bg-white/10 rounded-full px-3 py-1 text-white/80">
        <ShieldCheck className="w-3.5 h-3.5" /> {t.common.vatBadge}
      </div>

      <button
        onClick={() => setBookingOpen(true)}
        disabled={!result || result.price_incl_vat <= 0}
        className="mt-5 w-full py-4 rounded-full bg-white text-[#1E3A2F] font-bold text-base hover:bg-white/90 transition-colors disabled:opacity-50"
        data-testid="pax-book-btn"
      >
        {preliminary ? t.pax.bookPreliminary : t.pax.bookThis}
      </button>

      <BookingDialog
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        serviceType="pax"
        configuration={config}
        priceData={result}
        postcode={postcode}
        source={source}
        recognizedComponents={recognizedComponents}
        fileIds={fileIds}
      />
    </div>
  );
}
